import React, {createContext, useContext, useEffect, useState} from "react";
import {CartAdditionalData, CartType, ProductShoppingCart, ShoppingCart} from "./cart.dto";
import {
    getCart,
    addItemToCart,
    removeItemFromCart,
    updateItemQuantity, updateDeliveryMethod,
} from "./CartService";
import {jwtDecode} from "jwt-decode";
import {AuchanJwtPayload} from "@/GuardedRoute";
import { toast } from "../ui/use-toast";

export interface CartContextType {
    cart: CartType | null;
    setCart: (cart: CartType) => void;
    addToCart: (item: ProductShoppingCart, isTransit: boolean) => Promise<any>;
    removeFromCart: (itemId: number, isTransit: boolean) => Promise<any>;
    updateCartItemQuantity: (itemId: number, quantity: number, isTransit: boolean) => Promise<any>;
    fetchCart: (isPickup?: boolean) => Promise<void>;
    updateDeliveryMethodInCart: (isPickup: boolean) => Promise<void>;
}

interface CartAdditionalDataContextType {
    cartAdditionalData: CartAdditionalData;
    setCartAdditionalData: (additionalData: CartAdditionalData) => void;
}

interface CartProviderProps {
    children: React.ReactNode;
}

export const CartContext = createContext<CartContextType>({
    cart: null,
    setCart: () => {
    },
    addToCart: async () => {
    },
    removeFromCart: async () => {
    },
    updateCartItemQuantity: async () => {
    },
    fetchCart: async () => {
    },
    updateDeliveryMethodInCart: async () => {}
});
const CartAdditionalDataContext = createContext<CartAdditionalDataContextType | undefined>(undefined);

export const CartProvider: React.FC<CartProviderProps> = function ({children}) {
    const [cart, setCart] = useState<CartType | null>(null);
    const [cartAdditionalData, setCartAdditionalData] = useState<CartAdditionalData>({workingPointId: ''});

    const fetchCart = async (isPickup?: boolean) => {
        var accessToken = localStorage.getItem("token");
        if (!accessToken) {
            return;
        }

        const decodedToken = jwtDecode<AuchanJwtPayload>(accessToken!);
        const userRole = decodedToken["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"].toUpperCase();
        if (userRole !== 'BASIC') {
            return;
        }

        try {
            const fetchedCart = await getCart(isPickup);
            if (fetchedCart) {
                setCart({ ...fetchedCart, isPickup });
            }
        } catch (error) {
            console.error("Failed to fetch cart:", error);
        }
    };

    useEffect(() => {
        fetchCart();
    }, []);

    const addToCart = async (item: ProductShoppingCart, isTransit: boolean): Promise<any> => {
        if (!cart) {
            try {
                fetchCart(); // Refetch the cart to get the updated state
            } catch (error) {
                console.error("Failed to refresh the cart:", error);
            }
            return;
        }
        try {
            const simpleCart: ShoppingCart = {
                items: {
                    stockItems: cart.items.stockItems
                        .map(cartItem => ({
                            articleId: cartItem.articleId ?? cartItem.id,
                            quantity: cartItem.quantity,
                        })) as ProductShoppingCart[],
                    transitItems: cart.items.transitItems
                        .map(cartItem => ({
                            articleId: cartItem.articleId ?? cartItem.id,
                            quantity: cartItem.quantity,
                        })) as ProductShoppingCart[],
                },
            };
            const updatedCart = await addItemToCart(simpleCart, item, isTransit);
            setCart(updatedCart);
        }catch (error: any) {
            console.error("Failed to add item to cart:", error);

            if (error?.response?.status === 400) {
                console.warn("Cart out of sync. Refetching cart...");
                await fetchCart();
                toast({ description: "Coșul a fost sincronizat. Vă rugăm încercați din nou." });
                return;
            }

            throw error;
        }

    };

    const removeFromCart = async (itemId: number, isTransit: boolean): Promise<any> => {
        if (!cart) return;

        try {
            const simpleCart: ShoppingCart = {
                items: {
                    stockItems: cart.items.stockItems
                        .map(cartItem => ({
                            articleId: cartItem.articleId ?? cartItem.id,
                            quantity: cartItem.quantity,
                        })) as ProductShoppingCart[],
                    transitItems: cart.items.transitItems
                        .map(cartItem => ({
                            articleId: cartItem.articleId ?? cartItem.id,
                            quantity: cartItem.quantity,
                        })) as ProductShoppingCart[],
                },
            };

            const updatedCart = await removeItemFromCart(simpleCart, itemId, isTransit);
            setCart(updatedCart);
        } catch (error: any) {
            console.error("Failed to remove item from cart:", error);
            
            const status = error?.response?.status;
            if (status === 400 || status === 500) {
                console.warn("Cart out of sync after remove. Refetching cart...");
                await fetchCart();
                
                if (typeof window !== "undefined") {
                    const event = new CustomEvent("toast", {
                        detail: { message: "Coșul a fost sincronizat. Vă rugăm încercați din nou." },
                    });
                    window.dispatchEvent(event);
                }

                return;
            }

            throw error;
        }
    };

    const updateCartItemQuantity = async (itemId: number, quantity: number, isTransit: boolean): Promise<any> => {
        if (!cart) return;

        try {
            const simpleCart: ShoppingCart = {
                items: {
                    stockItems: cart.items.stockItems
                        .map(cartItem => ({
                            articleId: cartItem.articleId ?? cartItem.id,
                            quantity: cartItem.quantity,
                        })) as ProductShoppingCart[],
                    transitItems: cart.items.transitItems
                        .map(cartItem => ({
                            articleId: cartItem.articleId ?? cartItem.id,
                            quantity: cartItem.quantity,
                        })) as ProductShoppingCart[],
                },
            }
            const updatedCart = await updateItemQuantity(simpleCart, itemId, quantity, isTransit);
            setCart(updatedCart);
        } catch (error) {
            console.error("Failed to update item quantity:", error);
        }
    };

    const updateDeliveryMethodInCart = async (isPickup: boolean) => {
        if (!cart) return;

        try {
            const updatedCart = await updateDeliveryMethod(cart, isPickup);
            setCart(updatedCart);
        } catch (error) {
            console.error("Failed to update delivery method:", error);
        }
    };


    return (
        <CartContext.Provider value={{cart, setCart, addToCart, removeFromCart, updateCartItemQuantity, fetchCart, updateDeliveryMethodInCart}}>
            <CartAdditionalDataContext.Provider value={{cartAdditionalData, setCartAdditionalData}}>
                {children}
            </CartAdditionalDataContext.Provider>
        </CartContext.Provider>
    );
};

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);

    if (!context) {
        throw new Error("useCart must be used within a CartProvider component");
    }
    return context;
};

export const useCartAdditionalData = (): CartAdditionalDataContextType => {
    const context = useContext(CartAdditionalDataContext);
    if (!context) {
        throw new Error("useCartAdditionalData must be used within a CartProvider component");
    }
    return context;
}